import { missingDate, missingField } from 'app/constants';
import { IOrderTargetMargin } from '../Order.redux';
import { formatIfFractional } from 'app/helpers/numberHelper';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { IOrderPayPackages } from '../types';
import { IPayPackageCategory } from 'app/components/MarginTool/enum';

export const formatOrderRequirements = (unit, isRequired: Boolean) => {
  const NewLine = '\r\n';
  let qualification: string = '';

  if (unit?.experience?.skillSets?.some(x => x.isRequired === isRequired)) {
    qualification += 'Experience: ';
    qualification += unit?.experience?.skillSets
      .filter(x => x?.isRequired === isRequired)
      .map(
        x =>
          `${x?.discipline}, ${x?.specialty}${
            x?.subSpecialtyId && x?.subSpecialtyId > 0 ? `, ${x?.subSpecialty}` : ''
          }${x?.yearsOfExperience && x?.yearsOfExperience > 0 ? `, ${x?.yearsOfExperience} Years` : ''}`,
      )
      .join('; ');

    qualification += unit?.experience?.isNewGrad ? `, New Grad${NewLine}` : NewLine;
  }

  if (unit?.travelExperience?.isRequired === isRequired) {
    qualification += 'Travel Experience: ';
    qualification +=
      unit?.travelExperience?.yearsOfExperience > 0 ? `${unit?.travelExperience?.yearsOfExperience} Years` : '';
    qualification +=
      unit?.travelExperience?.numberOfAssignments > 0
        ? `${unit?.travelExperience?.numberOfAssignments} Assignments`
        : '';
    qualification += unit?.travelExperience?.inSpecialty ? ', In Specialty' : '';
    qualification += unit?.travelExperience?.general ? ', General' : '';
    qualification += NewLine;
  }

  if (unit?.qualification != null) {
    if (isRequired) {
      const licenseState = [];
      licenseState?.push(
        unit?.qualification?.activeLicense && unit?.facilityState ? `Active ${unit?.facilityState} License,  ` : '',
      );
      licenseState.push(unit?.qualification?.activeLicense && !unit?.facilityState ? 'Active License,  ' : '');
      licenseState.push(unit.qualification?.compactLicense ? 'Compact License, ' : '');
      licenseState.push(unit.qualification?.activeLicense || unit.qualification?.compactLicense ? NewLine : '');
      qualification += licenseState?.join('').replace(/,$/, '');
    }

    const hasCertifications = unit?.qualification?.certifications?.some(x => x?.isRequired === isRequired);
    qualification += hasCertifications ? 'Certifications: ' : '';

    const filteredCert = unit?.qualification?.certifications?.filter(x => x?.isRequired === isRequired);
    if (filteredCert) {
      qualification += filteredCert
        .map(cert => `${cert?.abbreviation}${cert?.documentAttachmentRequired ? ' (copy needed)' : ''}`)
        .join('; ');
    }
    qualification += hasCertifications ? NewLine : '';

    const hasLicenses = unit?.qualification?.licenses?.some(x => x?.isRequired === isRequired);
    qualification += hasLicenses ? 'Licenses: ' : '';
    const filteredLicenses = unit?.qualification?.licenses?.filter(x => x?.isRequired === isRequired);
    if (filteredLicenses) {
      qualification += filteredLicenses
        .map(license => `${license?.licenseAbbreviation}${license?.documentAttachmentRequired ? ' (copy needed)' : ''}`)
        .join('; ');
    }
    qualification += hasLicenses ? NewLine : '';

    const hasChecklists = unit?.qualification?.skillChecklists?.some(x => x?.isRequired === isRequired);
    qualification += hasChecklists ? 'Skills: ' : '';

    const filteredSkills = unit?.qualification?.skillChecklists?.filter(x => x?.isRequired === isRequired);
    if (filteredSkills) {
      qualification += filteredSkills
        .map(skill => `${skill?.text}${skill?.yearsOfExperience > 0 ? `, ${skill?.yearsOfExperience} Years` : ''}`)
        .join('; ');
    }
    qualification += hasChecklists ? NewLine : '';

    if (unit.qualification.emr?.isRequired === isRequired && unit?.qualification?.emr?.values?.length) {
      qualification += 'EMR: ';
      qualification += unit?.qualification?.emr?.values.join('; ');
      qualification += NewLine;
    }
  }

  const filteredNSR = unit?.nonStandardRequirement?.filter(x => x?.isRequired === isRequired);
  if (filteredNSR) {
    filteredNSR.forEach(nsr => {
      qualification += `${nsr?.category}: ${nsr?.value}${
        nsr?.yearsOfExperience ? `, ${nsr?.yearsOfExperience} Years` : ''
      }${nsr?.documentAttachmentRequired ? ' (copy needed)' : ''}`;
      qualification += NewLine;
    });
  }
  return qualification;
};

export const formatOrderTargetMargins = (data: IOrderTargetMargin[]): IOrderTargetMargin[] => {
  return data.map(item => ({
    ...item,
    subSpecialty: item.subSpecialty ?? missingField,
    targetMarginDefault: item.targetMarginDefault ?? 0,
    targetMarginDayShift: item.targetMarginDayShift ?? 0,
    targetMarginNightShift: item.targetMarginNightShift ?? 0,
    targetMarginDayNightShift: item.targetMarginDayNightShift ?? 0,
    payRangeMinMaxPercentage: item.payRangeMinMaxPercentage ?? 0,
    defaultWeeklyPay: item.defaultWeeklyPay ?? 0,
  }));
};

export const generateIds = (data: IOrderTargetMargin[]): IOrderTargetMargin[] => {
  return data.map((item, index) => ({
    ...item,
    rowId: index + 1,
  }));
};
export const filterActiveTargetMargins = (data: IOrderTargetMargin[]): IOrderTargetMargin[] => {
  //remove mode = 2 , inactive records from current state
  const activeTargetMargins: IOrderTargetMargin[] = (data || []).filter((item: IOrderTargetMargin) => item.mode !== 2);
  return activeTargetMargins;
};

export const transformTargetMargins = (data: IOrderTargetMargin[]): IOrderTargetMargin[] => {
  const activeTargetMargins = filterActiveTargetMargins(data);
  const formatted = formatOrderTargetMargins(activeTargetMargins);
  const withIds = generateIds(formatted);
  return withIds;
};

export const transformWeeklyGrossPay = (data: any[]) => {
  const transformed = Object.values(
    data.reduce((acc, curr) => {
      const key = `${curr.disciplineId}-${curr.specialtyId}-${curr.subSpecialtyId}`;

      if (!acc[key]) {
        acc[key] = {
          disciplineId: curr.disciplineId,
          specialtyId: curr.specialtyId,
          disciplineAbbr: curr.disciplineAbbr,
          specialtyAbbr: curr.specialtyAbbr,
          subSpecialtyId: curr.subSpecialtyId,
          subSpecialtyAbbr: curr.subSpecialtyAbbr,
          discipline: curr.discipline,
          specialty: curr.specialty,
          subSpecialty: curr.subSpecialty,
          skillset: `${curr.disciplineAbbr ?? curr.discipline ?? missingField} - ${
            curr.specialtyAbbr ?? curr.specialty ?? missingField
          }${
            curr.subSpecialtyAbbr ? ' - ' + curr.subSpecialtyAbbr : curr.subSpecialty ? ' - ' + curr.subSpecialty : ''
          }`,
          items: [],
        };
      }

      acc[key].items.push({
        orderId: curr.orderId,
        disciplineId: curr.disciplineId,
        disciplineAbbr: curr.disciplineAbbr,
        specialtyId: curr.specialtyId,
        specialtyAbbr: curr.specialtyAbbr,
        subSpecialtyId: curr.subSpecialtyId,
        shift: curr.shift ?? missingField,
        MRH: curr.minimumRequiredHours ?? missingField,
        WeeklyGross: formatIfFractional(curr.weeklyGrossPay, 2, '$'),
        PayRange:
          isNullOrUndefined(curr.minimumWeeklyGrossPay) && isNullOrUndefined(curr.maximumWeeklyGrossPay)
            ? missingField
            : `${formatIfFractional(curr.minimumWeeklyGrossPay, 2, '$')} - ${formatIfFractional(
                curr.maximumWeeklyGrossPay,
                2,
                '$',
              )}`,
        status: curr.publishingStatus ?? missingField,
        jdhPostingStatus: curr.jdhPostingStatus,
        jdhPostingId: curr.jdhPostingId,
        scenarioId: curr.scenarioId ?? null,
      });

      return acc;
    }, {}),
  );
  return transformed;
};
export const transformOrderPayPackages = (data: IOrderPayPackages[]) => {
  const transformed = data.map(item => ({
    ...item,
    skillset: `${item.disciplineAbbr ?? item.discipline ?? missingField} - ${
      item.specialtyAbbr ?? item.specialty ?? missingField
    }${item.subSpecialty ? ' - ' + item.subSpecialty : ''}`,
    updatedByName:
      item.updatedUserFirst || item.updatedUserLast ? `${item.updatedUserFirst} ${item.updatedUserLast}` : missingField,
  }));
  return transformed;
};

const safeSum = (values: Array<number | null | undefined>) =>
  values.reduce((acc, v) => acc + (typeof v === 'number' && !Number.isNaN(v) ? v : 0), 0);

const buildSkillSet = (item: any, prefix: 'original' | 'current') => {
  const discipline =
    prefix === 'original'
      ? item.originalDisciplineAbbr ?? item.originalDiscipline ?? item.disciplineAbbr ?? item.discipline
      : item.disciplineAbbr ?? item.discipline;
  const specialty =
    prefix === 'original'
      ? item.originalSpecialtyAbbr ?? item.originalSpecialty ?? item.specialtyAbbr ?? item.specialty
      : item.specialtyAbbr ?? item.specialty;
  const sub =
    prefix === 'original'
      ? item.originalSubSpecialtyAbbr ?? item.originalSubSpecialty ?? item.subSpecialtyAbbr ?? item.subSpecialty
      : item.subSpecialtyAbbr ?? item.subSpecialty;

  const disciplineStr = discipline ?? missingField;
  const specialtyStr = specialty ?? missingField;
  const subStr = sub ? ` - ${sub}` : '';
  return `${disciplineStr} - ${specialtyStr}${subStr}`;
};

const buildName = (...parts: Array<string | null | undefined>) => {
  const name = parts.filter(Boolean).join(' ').trim();
  return name.length > 0 ? name : missingField;
};

export const transformPlacementPayPackages = (data: any[]) => {
  if (!Array.isArray(data)) return [];

  // Heuristic: if API already returns splitId per item, treat it as split-per-row response.
  const apiAlreadySplitPerRow = data.some(x => !isNullOrUndefined((x as any)?.splitId));

  const expanded = apiAlreadySplitPerRow
    ? data
    : data.flatMap(item => {
        const splits = item?.scenario?.splits;
        if (Array.isArray(splits) && splits.length > 0) {
          return splits.map(split => ({
            ...item,
            splitId: split.splitId,
            split: split,
          }));
        }
        return [item];
      });

  return expanded
    .filter(item => {
      const status = `${item?.scenarioStatus ?? item?.scenario?.scenarioStatus ?? ''}`.toLowerCase();
      const mode = item?.mode ?? item?.scenario?.mode;
      return status !== 'deleted' && mode !== 2;
    })
    .map((item, indexCounter) => {
      const split = item?.split ?? null;
      const assignment = item?.assignment ?? item?.scenario?.assignment ?? null;
      const detailsValues = assignment?.payPackageDetailsValues ?? [];
      const isArchivedRaw = item?.isArchived ?? item?.archived ?? item?.isArchivedPlacement ?? item?.archive;
      const normalizedIsArchived =
        isArchivedRaw === true || isArchivedRaw === 1 || isArchivedRaw === 'Y' || isArchivedRaw === 'true';

      const bonusAmounts = (detailsValues || [])
        .filter(v => v?.categoryId === IPayPackageCategory.BonusType)
        .map(v => v?.payPackageValues?.amount);
      const reimbursementAmounts = (detailsValues || [])
        .filter(v => v?.categoryId === IPayPackageCategory.Reimbursements)
        .map(v => v?.payPackageValues?.amount);

      const numberOfSplits =
        Array.isArray(item?.scenario?.splits) && item?.scenario?.splits?.length > 0
          ? item?.scenario?.splits?.length
          : !isNullOrUndefined(item?.splitId)
            ? 1
            : 0;

      const row = {
        ...item,
        id: `${item?.placementId ?? 'pid'}-${item?.scenarioId ?? 'sid'}-${item?.splitId ?? split?.splitId ?? 0}-${indexCounter}`,
        indexCounter,
        orderId: item?.orderId ?? item?.orderID ?? item?.order?.orderId ?? item?.order?.id ?? null,
        // Candidate drawer expects selectedorder.row.candidateId + selectedorder.row.brandId
        candidateId: item?.travelerId ?? item?.candidateId ?? null,
        travelerId: item?.travelerId ?? item?.candidateId ?? null,
        brandId: item?.brandId ?? item?.companyId ?? item?.candidateBrandId ?? null,
        previewDrawerViewerId: item?.travelerId ?? item?.candidateId ?? null,
        previewDrawerViewerType: 1, // Required for XGridSideBarViewCell (Candidate side panel)

        candidateName: buildName(item?.candidateNameFirst, item?.candidateNameMiddle, item?.candidateNameLast),
        createdByName: buildName(item?.createdUserFirst, item?.createdUserLast),
        approvedByName: buildName(item?.approvedUserFirst, item?.approvedUserLast),
        verifiedByName: buildName(item?.verifiedUserFirst, item?.verifiedUserLast),
        updatedByName: buildName(item?.updatedUserFirst, item?.updatedUserLast),

        // Requirement: column name is "Skill Set" but the value should be the Placement Skill Set (original skillset)
        skillSet: buildSkillSet(item, 'original'),
        // Keep current skillset available in data (not shown as a dedicated column)
        placementSkillSet: buildSkillSet(item, 'current'),

        numberOfSplits,
        totalBonuses: safeSum(bonusAmounts),
        // Per grid requirement: only sum reimbursement amounts entered in Reimbursements (exclude travel payouts)
        totalReimbursements: safeSum(reimbursementAmounts),

        // Prefer split-level values when present
        splitId: item?.splitId ?? split?.splitId ?? null,
        splitStartDate: item?.effectiveStartDate ?? split?.effectiveStartDate ?? item?.splitStartDate ?? null,
        splitEndDate: item?.effectiveEndDate ?? split?.effectiveEndDate ?? item?.splitEndDate ?? null,
        weeklyGrossPaySplit: split?.weeklyGrossPay ?? item?.weeklyGrossPaySplit ?? item?.weeklyGrossPay ?? null,
        billRate: split?.billRate ?? item?.billRate ?? null,
        vms: split?.vms ?? item?.vms ?? null,
        other: split?.other ?? item?.other ?? null,
        payRate: split?.payRate ?? item?.payRate ?? null,
        shift: split?.shift ?? item?.shift ?? null,
        hoursPerWeek: split?.hoursPerWeek ?? item?.hoursPerWeek ?? null,
        hoursPerShift: split?.hoursPerShift ?? item?.hoursPerShift ?? null,
        lodgingPerDiem: split?.lodgingPerDiem ?? item?.lodgingPerDiem ?? null,
        mealPerDiem: split?.mealPerDiem ?? item?.mealPerDiem ?? null,
        housingType: split?.housingType ?? item?.housingType ?? null,
        placementStatus: item?.activityStatus ?? item?.placementStatus ?? item?.status ?? null,
        placementStatusId: item?.activityStatusId ?? item?.placementStatusId ?? item?.statusId ?? null,
        activityStatus: item?.activityStatus ?? null,
        activityStatusId: item?.activityStatusId ?? null,
        isArchived: normalizedIsArchived,
        archived: normalizedIsArchived,
      };

      return row;
    });
};
export const reOrderToOriginal = (original, updated) => {
  // Create a quick lookup map for updated data by scenarioId
  const updatedMap = new Map(updated.map(obj => [obj.scenarioId, obj]));

  // Rebuild the array based on the original order
  return original.map(orig => {
    const updatedItem = updatedMap.get(orig.scenarioId) as Object;
    return updatedItem ? { ...updatedItem } : {};
  });
};

// Method to replace items in an array (order remains unchanged)
export const replaceItemsByProperty = (original, updates, key) => {
  return original.map(org => {
    const updated = updates.find(u => u[key] === org[key]);
    return updated ? updated : org;
  });
};
